import { Accordion, Badge, Chip, Group, Stack, Text, TextInput } from "@mantine/core";
import { IconCircleCheck, IconSearch } from "@tabler/icons-react";
import { useMemo, useState } from "react";

import type { VhdpDevice, VhdpPlanData } from "./vhdpPlanData";

import { deviceImageUrl } from "./deviceImages";
import styles from "./DevicesTab.module.css";
import { adhLabel, coverageLabel, formatServiceFee } from "./vhdpPlanData";

/** Fixed-size catalog thumbnail. Keeps the slot even when a device has no image so rows stay aligned. */
const DeviceThumb = ({ name }: { name: string }) => {
    const src = deviceImageUrl(name);
    return (
        <div className={styles.thumb}>{src && <img src={src} alt="" loading="lazy" className={styles.thumbImg} />}</div>
    );
};

const ALL = "all";

/**
 * The expanded panel for one device. Everything shown is lifted straight from the sheet: the ADH
 * marker (KEY: Accidental Damage from Handling), the per-claim service fee, the sheet's sub-type list,
 * and any per-line caveats. The one-line coverage descriptor lives on the collapsed row (see
 * `coverageLabel`); this panel only spells out the ADH add-on when the sheet marks it.
 */
const DeviceDetail = ({ device }: { device: VhdpDevice }) => (
    <Stack gap={12} className={styles.detail}>
        {device.alsoIncludes && device.alsoIncludes.length > 0 && (
            <div>
                <Text className={styles.detailHeading}>Also includes</Text>
                <Group gap={8} mt={6}>
                    {device.alsoIncludes.map((item) => (
                        <Badge key={item} variant="default" radius="xl" className={styles.pill}>
                            {item}
                        </Badge>
                    ))}
                </Group>
            </div>
        )}

        {device.adh && (
            <Group gap={8} align="center" wrap="nowrap" className={styles.summaryRow}>
                <IconCircleCheck size={16} className={styles.okIcon} />
                <Text className={styles.summaryText}>{adhLabel(device)}</Text>
            </Group>
        )}

        <Text className={styles.feeLine}>Service fee: {formatServiceFee(device.serviceFee)} per approved claim</Text>

        {device.notes?.map((note) => (
            <Text key={note} className={styles.note}>
                {note}
            </Text>
        ))}
    </Stack>
);

export interface DevicesTabProps {
    data: VhdpPlanData;
    /** Device id to render pre-expanded (stories/tests; the accordion is otherwise all-collapsed). */
    defaultOpenDeviceId?: string;
}

/**
 * "Devices & service fees" — the searchable, category-filterable coverage matrix. A flat accordion of
 * every device on the sheet; each row shows its per-claim service fee and, where the sheet marks it,
 * accidental-damage coverage. Expands to the full per-line detail. Search + category state is local.
 */
export const DevicesTab = ({ data, defaultOpenDeviceId }: DevicesTabProps) => {
    const { categories, devices } = data;
    const [query, setQuery] = useState("");
    const [category, setCategory] = useState<string>(ALL);

    const countFor = (categoryId: string) =>
        categoryId === ALL ? devices.length : devices.filter((d) => d.categoryId === categoryId).length;

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return devices.filter(
            (device) =>
                (category === ALL || device.categoryId === category) &&
                (q === "" || device.name.toLowerCase().includes(q)),
        );
    }, [devices, query, category]);

    const categoryName = (categoryId: string) => categories.find((c) => c.id === categoryId)?.name ?? "";

    return (
        <Stack gap={16} className={styles.root}>
            <TextInput
                type="search"
                size="sm"
                label="Search devices"
                placeholder="e.g. TVs, laptops, smartwatches"
                value={query}
                onChange={(e) => setQuery(e.currentTarget.value)}
                leftSection={<IconSearch size={18} />}
                classNames={{ label: styles.searchLabel, input: styles.searchInput }}
            />

            <div>
                <Text component="h3" className={styles.blockHeading} id="vhdp-browse-label">
                    Category
                </Text>
                <Chip.Group multiple={false} value={category} onChange={setCategory}>
                    <Group gap={8} role="group" aria-labelledby="vhdp-browse-label">
                        <Chip value={ALL} variant="outline" radius="xl" color="asurion">
                            All devices<span className={styles.chipCount}>{countFor(ALL)}</span>
                        </Chip>
                        {categories.map((cat) => (
                            <Chip key={cat.id} value={cat.id} variant="outline" radius="xl" color="asurion">
                                {cat.name}
                                <span className={styles.chipCount}>{countFor(cat.id)}</span>
                            </Chip>
                        ))}
                    </Group>
                </Chip.Group>
            </div>

            <Text role="status" className={styles.resultCount}>
                {filtered.length} device{filtered.length === 1 ? "" : "s"}
            </Text>

            {filtered.length === 0 ? (
                <Text className={styles.empty}>No devices match “{query.trim()}”.</Text>
            ) : (
                <Accordion
                    variant="contained"
                    radius="md"
                    chevronPosition="right"
                    defaultValue={defaultOpenDeviceId}
                    classNames={{ chevron: styles.chevron }}
                >
                    {filtered.map((device) => (
                        <Accordion.Item key={device.id} value={device.id} className={styles.item}>
                            <Accordion.Control classNames={{ label: styles.controlLabel }}>
                                <Group align="center" wrap="nowrap" gap="md">
                                    <DeviceThumb name={device.name} />
                                    <Stack gap={2} className={styles.nameCol}>
                                        <Text className={styles.deviceName}>{device.name}</Text>
                                        <Text className={styles.deviceCategory}>{categoryName(device.categoryId)}</Text>
                                    </Stack>
                                    <Stack gap={2} align="flex-start" className={styles.factsCol}>
                                        <Text className={styles.fee}>{formatServiceFee(device.serviceFee)}</Text>
                                        <Text className={styles.coverage}>{coverageLabel(device)}</Text>
                                    </Stack>
                                </Group>
                            </Accordion.Control>
                            <Accordion.Panel>
                                <DeviceDetail device={device} />
                            </Accordion.Panel>
                        </Accordion.Item>
                    ))}
                </Accordion>
            )}
        </Stack>
    );
};
